import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Heart } from "lucide-react";
import { CheersGlasses } from "./CheersGlasses";

const TARGET = new Date("2026-11-20T19:00:00+05:30").getTime();

const BUBBLES = Array.from({ length: 18 }, (_, i) => ({
  left: `${(i * 61) % 100}%`,
  size: 5 + ((i * 13) % 16),
  duration: 9 + ((i * 7) % 10),
  delay: (i * 1.7) % 9,
}));

const STARS = Array.from({ length: 14 }, (_, i) => ({
  left: `${(i * 37 + 6) % 96}%`,
  top: `${(i * 23 + 4) % 88}%`,
  size: 6 + ((i * 5) % 10),
  delay: (i * 0.6) % 3,
}));

const useCountdown = () => {
  const [diff, setDiff] = useState(TARGET - Date.now());
  useEffect(() => {
    const id = setInterval(() => setDiff(TARGET - Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const d = Math.max(0, diff);
  return {
    days: Math.floor(d / 86400000),
    hours: Math.floor((d / 3600000) % 24),
    minutes: Math.floor((d / 60000) % 60),
    seconds: Math.floor((d / 1000) % 60),
  };
};

const scrollTo = (id) => {
  if (window.__lenis) window.__lenis.scrollTo(`#${id}`, { duration: 1.4 });
  else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

const DecoArcs = () => (
  <svg viewBox="0 0 600 320" fill="none" className="w-[560px] max-w-[92vw] opacity-70">
    {[280, 230, 180, 132].map((r, i) => (
      <path
        key={r}
        d={`M ${300 - r} 300 A ${r} ${r} 0 0 1 ${300 + r} 300`}
        stroke="#E0C075"
        strokeWidth="1"
        opacity={0.35 - i * 0.06}
        strokeDasharray={i % 2 ? "3 7" : undefined}
      />
    ))}
    {Array.from({ length: 9 }, (_, i) => {
      const angle = Math.PI + (i * Math.PI) / 8;
      const x1 = 300 + Math.cos(angle) * 120;
      const y1 = 300 + Math.sin(angle) * 120;
      const x2 = 300 + Math.cos(angle) * 292;
      const y2 = 300 + Math.sin(angle) * 292;
      return (
        <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#E0C075" strokeWidth="0.7" opacity="0.18" />
      );
    })}
  </svg>
);

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { days, hours, minutes, seconds } = useCountdown();
  const { scrollY } = useScroll();
  const arcsY = useTransform(scrollY, [0, 700], [0, 150]);
  const glowY = useTransform(scrollY, [0, 700], [0, 60]);
  const units = [
    ["Days", days],
    ["Hours", hours],
    ["Minutes", minutes],
    ["Seconds", seconds],
  ];

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0B0C10]"
    >
      {/* ambient glows */}
      <motion.div
        className="absolute inset-0"
        style={{
          y: glowY,
          background:
            "radial-gradient(ellipse 62% 46% at 50% 26%, rgba(224,192,117,0.16), transparent 70%)",
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 40% 34% at 8% 92%, rgba(74,29,72,0.5), transparent 70%), radial-gradient(ellipse 40% 34% at 92% 92%, rgba(74,29,72,0.5), transparent 70%)",
        }}
      />

      {/* twinkling stars */}
      <div className="absolute inset-0" aria-hidden="true">
        {STARS.map((s, i) => (
          <span
            key={i}
            className="star-twinkle absolute text-amber-300"
            style={{ left: s.left, top: s.top, animationDelay: `${s.delay}s` }}
          >
            <svg width={s.size} height={s.size} viewBox="0 0 10 10" fill="currentColor">
              <path d="M5 0 L6 4 L10 5 L6 6 L5 10 L4 6 L0 5 L4 4 Z" />
            </svg>
          </span>
        ))}
      </div>

      {/* floating bubbles */}
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        {BUBBLES.map((b, i) => (
          <span
            key={i}
            className="bubble"
            style={{
              left: b.left,
              width: b.size,
              height: b.size,
              animationDuration: `${b.duration}s`,
              animationDelay: `${b.delay}s`,
            }}
          />
        ))}
      </div>

      {/* art-deco arcs with parallax drift */}
      <motion.div
        style={{ y: arcsY }}
        className="absolute top-[8%] left-0 right-0 flex justify-center pointer-events-none"
        aria-hidden="true"
      >
        <DecoArcs />
      </motion.div>

      <div className="relative z-10 max-w-3xl mx-auto px-6 py-24 text-center">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="font-mono-accent text-xs uppercase tracking-[0.3em] text-amber-400"
        >
          Karan, Avneet &amp; Alagh Family cordially invite you to
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mt-6 flex justify-center"
        >
          <CheersGlasses className="w-48 h-28 sm:w-64 sm:h-36 float-soft" />
        </motion.div>

        <h1
          data-testid="hero-event-title"
          className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-amber-100 mt-5"
        >
          <MaskedLine delay={0.3}>The Cocktail</MaskedLine>
          <MaskedLine delay={0.48} className="gold-text italic">
            Evening
          </MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-couple-names"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.65 }}
          className="font-serif-display text-xl sm:text-2xl text-zinc-200 mt-4 flex items-center justify-center gap-3"
        >
          Sarthak <Heart className="w-4 h-4 text-amber-400 fill-amber-400" /> Tanishka
        </motion.p>

        <motion.div
          data-testid="hero-date-badge"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.78 }}
          className="mt-6 inline-flex items-center gap-3 card-glass rounded-full px-6 py-2.5 text-sm sm:text-base text-amber-200"
        >
          <MapPin className="w-4 h-4 text-amber-400" />
          Friday, 20th November 2026 · 7:00 PM Onwards · Tilak Nagar, New Delhi
        </motion.div>

        <motion.div
          data-testid="hero-countdown-timer"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-10 flex justify-center gap-3 sm:gap-5"
        >
          {units.map(([label, value]) => (
            <div key={label} className="card-glass rounded-xl px-3 py-3 sm:px-5 min-w-[70px] sm:min-w-[88px]">
              <div className="font-serif-display text-2xl sm:text-4xl font-bold gold-text tabular-nums">
                {String(value).padStart(2, "0")}
              </div>
              <div className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-1">
                {label}
              </div>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.02 }}
          className="mt-10 flex flex-wrap justify-center gap-4"
        >
          <button
            data-testid="hero-view-venue-btn"
            onClick={() => scrollTo("details")}
            className="btn-outline-gold rounded-full px-8 py-3 font-semibold text-sm sm:text-base"
          >
            View Venue
          </button>
        </motion.div>
      </div>
    </section>
  );
};
